import React, { useState, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import { Breadcrumb } from '../../components/Common'
import { Upload, Download, FileSpreadsheet } from 'lucide-react'
import prospectMasterService from '../../services/prospectMasterService'

const MAX_FILE_SIZE = 10 * 1024 * 1024 // 10MB
const ACCEPT = '.xlsx'

const UploadProspect = () => {
  const navigate = useNavigate()
  const fileInputRef = useRef(null)
  const [file, setFile] = useState(null)
  const [dragActive, setDragActive] = useState(false)
  const [uploading, setUploading] = useState(false)

  const breadcrumbItems = [
    { label: 'CRM', path: '/crm', onClick: () => navigate('/crm') },
    { label: 'Masters', path: '/crm', onClick: () => navigate('/crm') },
    { label: 'Upload Prospect', path: '/crm/upload-prospect', isCurrent: true }
  ]

  const handleDownloadTemplate = async () => {
    try {
      await prospectMasterService.downloadProspectTemplate()
      toast.success('Template downloaded successfully.')
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to download template.')
    }
  }

  const handleDrag = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setDragActive(e.type === 'dragenter' || e.type === 'dragover')
  }

  const validateFile = (f) => {
    if (!f) return null
    if (!f.name.toLowerCase().endsWith('.xlsx')) {
      toast.error('Only Excel (.xlsx) files are allowed.')
      return null
    }
    if (f.size > MAX_FILE_SIZE) {
      toast.error('File size must be 10MB or less.')
      return null
    }
    return f
  }

  const handleDrop = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setDragActive(false)
    const dropped = e.dataTransfer?.files?.[0]
    const valid = validateFile(dropped)
    if (valid) setFile(valid)
  }

  const handleFileSelect = (e) => {
    const selected = e.target?.files?.[0]
    const valid = validateFile(selected)
    if (valid) setFile(valid)
    e.target.value = ''
  }

  const handleChooseFile = () => {
    fileInputRef.current?.click()
  }

  const handleUpload = async () => {
    if (!file) {
      toast.warning('Please choose a file first.')
      return
    }
    try {
      setUploading(true)
      const res = await prospectMasterService.uploadProspects(file)
      const data = res?.data || {}
      const created = data.created ?? 0
      const failed = data.failed ?? 0
      if (created > 0) {
        toast.success(
          failed > 0
            ? `${created} prospect(s) created. ${failed} row(s) failed. Check details below.`
            : `${created} prospect(s) created successfully.`
        )
        setFile(null)
      } else if (failed > 0) {
        toast.error(`No prospects created. ${failed} row(s) had errors.`)
      } else {
        toast.info('No data rows found in the file.')
      }
      if (data.failedDetails?.length) {
        console.warn('Upload failed rows:', data.failedDetails)
      }
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'Upload failed.'
      toast.error(msg)
    } finally {
      setUploading(false)
    }
  }

  return (
    <div className="sales-dashboard">
      <div className="breadcrumb-section">
        <Breadcrumb
          items={breadcrumbItems}
          rightSlot={
            <button
              type="button"
              className="btn btn-outline-primary btn-sm d-flex align-items-center gap-1"
              onClick={handleDownloadTemplate}
              title="Download Excel template"
            >
              <Download size={18} />
              <span className="d-none d-sm-inline">Download Template</span>
            </button>
          }
        />
      </div>

      <div className="page-header">
        <div className="page-title">
          <h1>Upload Prospect</h1>
          <p>Upload prospects from an Excel file. Use the template and fill required columns: Prospect Name, Prospect Category, Region.</p>
        </div>
      </div>

      <div className="form-container">
        <div className="sac-form-header">
          <h2>Upload File</h2>
        </div>

        <div
          className={`upload-zone ${dragActive ? 'upload-zone-active' : ''} ${file ? 'upload-zone-has-file' : ''}`}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          style={{
            border: '2px dashed var(--primary-color, #0d6efd)',
            borderRadius: '8px',
            padding: '32px 24px',
            textAlign: 'center',
            backgroundColor: dragActive ? 'rgba(13, 110, 253, 0.06)' : (file ? 'rgba(13, 110, 253, 0.04)' : 'transparent'),
            marginBottom: '20px'
          }}
        >
          <Upload size={48} style={{ color: 'var(--primary-color, #0d6efd)', marginBottom: '12px' }} />
          <h3 style={{ color: 'var(--primary-color, #0d6efd)', marginBottom: '8px', fontSize: '1.25rem' }}>Upload a File</h3>
          <p style={{ color: 'var(--color-text-secondary, #5B6478)', marginBottom: '4px' }}>Drag and drop your file here</p>
          <p style={{ color: 'var(--color-text-secondary, #5B6478)', fontSize: '0.875rem' }}>or click to browse</p>
          <p style={{ color: 'var(--color-text-secondary, #5B6478)', fontSize: '0.8rem', marginTop: '8px' }}>Supports .xlsx (Max 10MB)</p>
          {file && (
            <p className="mt-2 d-flex align-items-center justify-content-center gap-1" style={{ flexWrap: 'wrap' }}>
              <FileSpreadsheet size={18} />
              <span>{file.name}</span>
              <span style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary, #5B6478)' }}>
                ({(file.size / 1024).toFixed(1)} KB)
              </span>
            </p>
          )}
        </div>

        <input
          ref={fileInputRef}
          type="file"
          accept={ACCEPT}
          onChange={handleFileSelect}
          style={{ display: 'none' }}
        />

        <div className="d-flex justify-content-between flex-wrap gap-2">
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleChooseFile}
          >
            Choose File
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={handleUpload}
            disabled={!file || uploading}
          >
            {uploading ? 'Uploading...' : 'Upload File'}
          </button>
        </div>
      </div>
    </div>
  )
}

export default UploadProspect
